// ingredientNamingRules.js
// Single source of truth for ingredient naming examples used across all AI prompts.
// Imported by: promptGenerators.js, progressiveRecipeProcessor.js, recipeProcessingService.js, AlternativeRecipes.js

import { filterByRestrictions } from './dietaryFilters';

/**
 * Full list of ingredient naming examples for the meal-plan generation prompt (o3-mini).
 * Format: each line is a complete example string ready to be embedded in a prompt.
 */
export const INGREDIENT_NAMING_EXAMPLES = [
  '"chicken broth, vegetable broth, stew broth, fish broth, etc." (not "concentrated broth")',
  '"fresh dill, fresh spinach, fresh parsley, etc." (not "fresh herbs" or "herbs")',
  '"strawberry jam, peach jam, blueberry jam, etc." (not "fruit jam", not "berry jam", not "mixed fruit jam")',
  '"strawberry, peach, blueberry, orange etc." (not "fruit", not "berry")',
  '"corn meal" (not "polenta")',
  '"grana padano cheese, cheddar cheese, etc" (not "cheese")',
  '"extra virgin olive oil" (not "oil")',
  '"long grain rice, brown rice, basmati rice, etc." (not "rice")',
  '"whole milk, soy milk, almond milk, etc." (not "milk")',
  '"chicken breast, chicken fillets, etc." (not "chicken")',
  '"frozen mushrooms, portobello mushrooms, shiitake mushrooms, champinone mushrooms etc." (not "mushrooms")',
  '"beef sirloin, minced beef, beef fillets, etc." (not "beef")',
  '"pork tenderloin, pork ribs, pork belly, etc." (not "pork")',
  '"lamb chops, lamb fillets, etc." (not "lamb")',
  '"duck breast, duck fillets, etc." (not "duck")',
  '"white wine, red wine, rose wine, etc." (not "wine")',
  '"salmon, tilapia, sea bass, etc." (not "fish or white fish")',
  '"frozen spinach, fresh spinach, etc." (not "spinach")',
  '"ground paprika, ground cumin, ground coriander, ground basil, ground pepper, etc." (not "spices")',
  '"cinnamon sticks, ground cinnamon, etc." (not "cinnamon")',
  '"canned chickpeas, dried chickpeas, etc." (not "chickpeas")',
  '"baguette, whole wheat bread, sourdough loaf, etc." (not "bread")',
  '"plain flour, whole wheat flour, almond flour, etc." (not "flour")',
  '"spaghetti, penne, fusilli, etc." (not "pasta" or "premium pasta")',
  '"greek yogurt, soy yogurt, blueberry yogurt, etc." (not "yogurt")',
  '"piadina, tortilla wrap, pita bread, etc." (not "wrap")',
  '"mozzarella shredded cheese, fundir shredded cheese, emmental shredded cheese, grana padano shredded cheese, etc." (not "shredded cheese")',
  '"artisan pizza bases, gluten-free and lactose-free pizza bases, etc." (not "flatbread")',
];

/**
 * Returns the full naming examples block for the o3-mini meal plan prompt.
 * Filters out examples that conflict with user dietary restrictions.
 * @param {string[]} [restrictionKeys=[]] - Dietary restriction keys (e.g., ["noDairy", "noNuts"])
 */
export const getFullNamingExamplesBlock = (restrictionKeys = []) => {
  let examples = INGREDIENT_NAMING_EXAMPLES;
  
  if (restrictionKeys.length > 0) {
    examples = filterByRestrictions(examples, restrictionKeys);
  }
  
  return `   - CRITICAL: Use specific ingredients names, not generic ones. Specific ingredient names examples: 
      ${examples.join(', \n      ')}
      etc.`;
};

/**
 * Returns a compact naming rule for recipe details / alternative recipe prompts.
 * Includes the key principle + a representative subset of examples.
 */
export const getCompactNamingRule = () => {
  return `INGREDIENT NAMING (CRITICAL):
- Use EXACTLY the same ingredient names as provided in the ingredient list — do NOT rename, shorten, or generalize.
- In "ingredient_storage", each "name" must match the ingredient name EXACTLY as given.
- Do NOT add NEW ingredients to "ingredient_storage" or the shopping output if they are not in the provided list (no oil, water, etc. unless explicitly listed). EXCEPTION: cooking-step text may still say "season with salt and pepper to taste" as a basic seasoning action even when salt/pepper are not listed — but never add salt or pepper to "ingredient_storage".
- Specific names required: "whole milk" not "milk", "chicken breast fillets" not "chicken", "extra virgin olive oil" not "oil", "basmati rice" not "rice", "fresh basil" not "herbs", "ground paprika" not "spices", "spaghetti" not "pasta", "greek yogurt" not "yogurt".`;
};

/**
 * Returns a short inline naming hint for alternative recipes prompt (AlternativeRecipes.js).
 * Used inside the ingredients section of the prompt.
 */
export const getAlternativeNamingHint = () => {
  return `- Precise names: "whole milk" not "milk", "chicken breast fillets" not "chicken", "extra virgin olive oil" not "oil", "basmati rice" not "rice", "fresh basil" not "herbs"`;
};

/**
 * Allowed values for the productHint `form` enum.
 * Each ingredient in the meal plan MUST tag itself with one of these values so the
 * Typesense matcher can boost / exclude categories accordingly.
 */
export const PRODUCT_FORM_VALUES = [
  'fresh',
  'frozen',
  'canned',
  'jarred',
  'dried',
  'cured',
  'prepared',
  'dairy',
  'other',
];

/**
 * Returns the productHint + form rule block for the meal-plan generation prompt.
 * The block teaches the model the new schema fields and gives concrete
 * WRONG -> RIGHT examples that cover the most ambiguous ingredients
 * (tuna, chicken, beans, mushrooms, milk, tomatoes, bread, shrimp).
 *
 * HEAD-NOUN-FIRST ORDERING: productHint MUST start with the actual food item
 * (the "head noun" — salmon, onion, yogurt, salt, etc.), then add qualifiers,
 * processing, packaging, and brand-style attributes AFTER. Typesense Pass 1
 * uses `prioritize_exact_match` plus token-overlap scoring against
 * keywords/displayName/name. When the hint starts with a generic attribute
 * ("fresh", "fine", "plain", "canned", "whole"), those tokens dominate
 * ranking and can pull totally unrelated products (e.g. "fresh sliced
 * mushrooms" wrongly matched a fresh-cilantro product). Putting the head
 * noun first keeps ranking anchored on the actual food.
 */
export const getProductHintRule = () => {
  return `   - CRITICAL: every ingredient object MUST include BOTH "productHint" and "form".
      • productHint: short English noun phrase that names the EXACT supermarket-ready form to buy (the way it is labeled on the shelf). Include packaging form, processing state, cut, and brining/packing medium when relevant. Max ~60 chars.
        ★ HEAD-NOUN-FIRST RULE (non-negotiable): start the hint with the actual food item (the head noun: salmon, onion, yogurt, salt, mushrooms, tuna, etc.), then append qualifiers (fresh / fine / plain / canned / frozen / cured / boneless / sliced / whole / etc.), processing details, and packaging AFTER the head noun. This anchors Typesense ranking on the real food, not on generic attribute words.
      • form: one of ${PRODUCT_FORM_VALUES.map(v => '"' + v + '"').join(' | ')}. Pick the dominant storage/processing state of the product as sold at the local supermarket.
      WRONG -> RIGHT productHint examples (note the head-noun-first reorder):
        - "tuna" -> productHint "tuna canned light in olive oil" + form "canned"  (NOT "canned light tuna in olive oil", NOT "fresh tuna steak")
        - "chicken" -> productHint "chicken breast fillets boneless skinless" + form "fresh"  (NOT "boneless skinless chicken breast fillets")
        - "beans" -> productHint "white beans canned in water, drained" + form "canned"  (NOT "canned white beans in water")
        - "shrimp" -> productHint "shrimp frozen peeled cooked" + form "frozen"  (NOT "frozen peeled cooked shrimp")
        - "milk" -> productHint "milk whole cow 1L carton" + form "dairy"  (NOT "whole cow milk 1L carton")
        - "mushrooms" -> productHint "champignon mushrooms fresh sliced" + form "fresh"  (NOT "fresh sliced champignon mushrooms")
        - "tomatoes" -> productHint "tomatoes canned peeled whole 400g" + form "canned" (sauce) OR "tomatoes fresh vine" + form "fresh" (salad)
        - "bread" -> productHint "sandwich bread loaf sliced" + form "prepared"  (NOT "sliced sandwich bread loaf")
        - "rice" -> productHint "basmati rice long-grain white" + form "dried"  (NOT "long-grain white basmati rice")
        - "olives" -> productHint "green manzanilla olives pitted in brine, jar" + form "jarred"  (NOT "pitted green manzanilla olives in brine")
        - "onion" -> productHint "sweet onion fresh" + form "fresh"  (NOT "fresh sweet onion")
        - "sea salt" -> productHint "sea salt fine box" + form "other"  (NOT "fine sea salt box")
        - "greek yogurt" -> productHint "greek yogurt plain tub" + form "dairy"  (NOT "plain greek yogurt tub")
        - "salmon" -> productHint "salmon fillet fresh atlantic" + form "fresh"  (NOT "fresh atlantic salmon fillet")
        - "garlic" -> productHint "garlic head fresh whole" + form "fresh"  (ALWAYS use the WHOLE-HEAD product; the recipe quantity stays in cloves but the shopping product is a head/mesh bag. NOT "garlic clove", NOT "minced garlic jar", NOT "garlic powder".)
      Match productHint to the cooking use: tomato SAUCE -> canned/jarred form, fresh tomato SALAD -> fresh form. The "form" field MUST match the productHint (canned hint -> "canned" form, etc.).
      Garlic special case: ingredients[].quantity should stay in the natural recipe unit ("2 cloves" / "1/2 head"), but productHint MUST be "garlic head fresh whole" so the grocery matcher resolves to the same head/mesh-bag product across all 9 recipes.`;
};
